import { SAMPLE_RATE, encodeWav, loudness, mixToMono, wavSeconds } from "@/lib/own-voice";

/**
 * The microphone side of a person's own voice (ADR-117 in `platform`): record, and hand back the
 * WAV core-api takes. Everything a test can check is in `own-voice.ts`; this file only talks to
 * the browser.
 *
 * The browser records in its own format (`MediaRecorder`: Opus in WebM, or AAC in MP4). The take
 * is then decoded, brought to one channel at `SAMPLE_RATE`, and written as plain 16-bit sound, so
 * what goes up is the same whatever recorded it. Nothing is sent from here, and the take lives in
 * memory until the page lets it go (`Take.release`).
 *
 * Noise suppression and gain are left on: a rep records at their desk, on the floor, with the
 * headset they take calls on, and that is also what a lead will hear the voice against.
 */

/** A finished recording, as the WAV to send and an address the page can play it from. */
export interface Take {
  wav: Blob;
  seconds: number;
  url: string;
  /** Let the take go: its address stops working. */
  release: () => void;
}

export interface Recording {
  /** Stop and get the take. */
  stop: () => Promise<Take>;
  /** Stop and keep nothing. */
  cancel: () => void;
}

type WithWebkit = typeof globalThis & { webkitAudioContext?: typeof AudioContext };

/** What this browser has of the two things a recording needs. */
export function recordingSupport(): { microphone: boolean; recorder: boolean } {
  return {
    microphone: typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia),
    recorder: typeof MediaRecorder !== "undefined",
  };
}

/** Why the microphone could not be opened, in a sentence. */
export function microphoneProblem(error: unknown): string {
  const name = error instanceof DOMException ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "The browser did not let this page use the microphone. Allow it in the address bar, then try again.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "No microphone was found. Plug in your headset, then try again.";
  }
  if (name === "NotReadableError") {
    return "The microphone is in use by something else. Close it there, then try again.";
  }
  return "The microphone could not be opened. Try again.";
}

/** One channel at `SAMPLE_RATE`, from whatever the browser decoded. */
async function toSpeech(decoded: AudioBuffer): Promise<{ samples: Float32Array; rate: number }> {
  if (typeof OfflineAudioContext === "undefined") {
    // No resampler: the take keeps the rate it was decoded at, which core-api also takes.
    const channels = Array.from({ length: decoded.numberOfChannels }, (_, i) => decoded.getChannelData(i));
    return { samples: mixToMono(channels), rate: decoded.sampleRate };
  }
  const frames = Math.max(1, Math.ceil(decoded.duration * SAMPLE_RATE));
  const offline = new OfflineAudioContext(1, frames, SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();
  return { samples: rendered.getChannelData(0), rate: SAMPLE_RATE };
}

/**
 * Start recording from the microphone. `onLevel` is told how loud the room is, from 0 to 1, a few
 * times a second, for a meter. Rejects with the browser's own error when the microphone cannot be
 * opened (`microphoneProblem` words it).
 */
export async function startRecording(onLevel?: (level: number) => void): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: false, noiseSuppression: true, autoGainControl: true },
  });
  const Context = globalThis.AudioContext ?? (globalThis as WithWebkit).webkitAudioContext;
  const context = new Context();
  const recorder = new MediaRecorder(stream);
  const chunks: Blob[] = [];
  recorder.addEventListener("dataavailable", (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  });

  let frame = 0;
  if (onLevel) {
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    context.createMediaStreamSource(stream).connect(analyser);
    const window = new Float32Array(analyser.fftSize);
    const read = () => {
      analyser.getFloatTimeDomainData(window);
      onLevel(loudness(window));
      frame = requestAnimationFrame(read);
    };
    frame = requestAnimationFrame(read);
  }

  const close = () => {
    cancelAnimationFrame(frame);
    for (const track of stream.getTracks()) track.stop();
  };
  const stopped = new Promise<void>((resolve) => recorder.addEventListener("stop", () => resolve(), { once: true }));
  recorder.start();

  return {
    stop: async () => {
      if (recorder.state !== "inactive") recorder.stop();
      await stopped;
      close();
      try {
        const recorded = await new Blob(chunks, { type: recorder.mimeType }).arrayBuffer();
        const speech = await toSpeech(await context.decodeAudioData(recorded));
        const wav = new Blob([encodeWav(speech.samples, speech.rate)], { type: "audio/wav" });
        const url = URL.createObjectURL(wav);
        return { wav, seconds: wavSeconds(wav.size, speech.rate), url, release: () => URL.revokeObjectURL(url) };
      } finally {
        void context.close().catch(() => undefined);
      }
    },
    cancel: () => {
      if (recorder.state !== "inactive") recorder.stop();
      close();
      void context.close().catch(() => undefined);
    },
  };
}
